import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { Taxonomy, Term } from '@/routes/_app.taxonomies';

/**
 * Panel de asignación de taxonomías (categorías y etiquetas) en el editor de
 * un post. Carga las taxonomías que aplican al tipo de contenido y, por cada
 * una, permite seleccionar términos existentes o crear nuevos al vuelo.
 */
export function PostTermsPanel({
  postTypeKey, value, onChange,
}: {
  postTypeKey: string;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { data: taxonomies } = useQuery({
    queryKey: ['taxonomies', 'for', postTypeKey],
    queryFn: () => unwrap<Taxonomy[]>(api.get('/taxonomies', { params: { post_type: postTypeKey } })),
  });

  if (!taxonomies || taxonomies.length === 0) return null;

  return (
    <>
      {taxonomies.map((tax) => (
        <Card key={tax.key}>
          <CardHeader>
            <CardTitle className="text-sm">{tax.label_plural}</CardTitle>
          </CardHeader>
          <CardContent>
            <TaxonomyField taxonomy={tax} value={value} onChange={onChange} />
          </CardContent>
        </Card>
      ))}
    </>
  );
}

function TaxonomyField({
  taxonomy, value, onChange,
}: {
  taxonomy: Taxonomy;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');

  const { data: terms } = useQuery({
    queryKey: ['terms', taxonomy.key],
    queryFn: () => unwrap<Term[]>(api.get(`/taxonomies/${taxonomy.key}/terms`)),
  });

  const selected = useMemo(() => new Set(value), [value]);

  const create = useMutation({
    mutationFn: (name: string) =>
      unwrap<Term>(api.post(`/taxonomies/${taxonomy.key}/terms`, { name })),
    onSuccess: async (term) => {
      await qc.invalidateQueries({ queryKey: ['terms', taxonomy.key] });
      onChange([...value, term.id]);
      setDraft('');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'No se pudo crear');
    },
  });

  function toggle(id: string) {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  function addByName(raw: string) {
    const name = raw.trim();
    if (!name) return;
    const existing = (terms ?? []).find((t) => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!selected.has(existing.id)) onChange([...value, existing.id]);
      setDraft('');
      return;
    }
    create.mutate(name);
  }

  const ordered = useMemo(() => orderTerms(terms ?? [], taxonomy.hierarchical), [terms, taxonomy.hierarchical]);

  if (!taxonomy.hierarchical) {
    const selectedTerms = (terms ?? []).filter((t) => selected.has(t.id));
    return (
      <div className="space-y-2">
        {selectedTerms.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {selectedTerms.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs">
                {t.name}
                <button type="button" onClick={() => toggle(t.id)} aria-label={`Quitar ${t.name}`}>
                  <X className="size-3 text-neutral-500 hover:text-red-600" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <input
          list={`tax-${taxonomy.key}-options`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); addByName(draft); }
          }}
          placeholder="Escribe y pulsa Enter para añadir o crear…"
          className="h-9 w-full rounded-md border border-neutral-200 px-3 text-sm"
        />
        <datalist id={`tax-${taxonomy.key}-options`}>
          {(terms ?? []).filter((t) => !selected.has(t.id)).map((t) => (
            <option key={t.id} value={t.name} />
          ))}
        </datalist>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="max-h-56 space-y-0.5 overflow-y-auto">
        {ordered.length === 0 ? (
          <p className="text-xs text-neutral-400">Aún no hay {taxonomy.label_plural.toLowerCase()}.</p>
        ) : ordered.map(({ term, depth }) => (
          <label
            key={term.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-neutral-50"
            style={{ paddingLeft: `${depth * 16 + 4}px` }}
          >
            <input type="checkbox" checked={selected.has(term.id)} onChange={() => toggle(term.id)} className="size-3.5" />
            <span className="truncate">{term.name}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-1.5 border-t border-neutral-100 pt-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addByName(draft); } }}
          placeholder={`Nueva ${taxonomy.label_singular.toLowerCase()}…`}
          className="h-8 flex-1 rounded-md border border-neutral-200 px-2 text-sm"
        />
        <button
          type="button"
          onClick={() => addByName(draft)}
          disabled={!draft.trim() || create.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-neutral-900 px-2 py-1.5 text-xs text-white disabled:opacity-50"
        >
          <Plus className="size-3.5" /> Añadir
        </button>
      </div>
    </div>
  );
}

function orderTerms(terms: Term[], hierarchical: boolean): Array<{ term: Term; depth: number }> {
  if (!hierarchical) {
    return [...terms].sort((a, b) => a.name.localeCompare(b.name)).map((term) => ({ term, depth: 0 }));
  }
  const byParent = new Map<string | null, Term[]>();
  for (const t of terms) {
    const key = t.parent_id ?? null;
    const arr = byParent.get(key) ?? [];
    arr.push(t);
    byParent.set(key, arr);
  }
  const out: Array<{ term: Term; depth: number }> = [];
  const walk = (parent: string | null, depth: number) => {
    const children = (byParent.get(parent) ?? []).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    for (const term of children) {
      out.push({ term, depth });
      walk(term.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
